// Main issue recording chat — feeds customer profile + documents to agent
import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { AlertTriangle, CheckCircle, X } from 'lucide-react'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickReplyButtons } from './QuickReplyButtons'
import { TypingIndicator } from './TypingIndicator'
import { ChatScrollHandler } from './ChatScrollHandler'
import { useChat } from '@/hooks/useChat'
import { parseIssueRecorderResponse } from '@/lib/parse-agent-response'
import { uploadIssueFiles } from '@/lib/issue-file-upload'
import { buildFileContext } from '@/lib/file-context-builder'
import { buildCustomerProfileContext } from '@/lib/customer-context-builder'
import { getAgentForFeature } from '@/hooks/useOrgSettings'
import { detectPII, applyRedactions } from '@/lib/pii-detection'
import { PIIApprovalModal } from '@/components/pii/PIIApprovalModal'
import type { IssueRecorderOutput } from '@/types/ai'
import type { ChatMessage as ChatMessageType } from '@/types/issue'
import type { Customer, CustomerEntity, CustomerUser } from '@/types/customer'
import type { PIIItem, PIIDecision } from '@/types/pii'

interface IssueRecordingChatProps {
  onDataExtracted: (data: IssueRecorderOutput['extractedData']) => void
  onComplete: () => void
  onMessagesChange?: (messages: ChatMessageType[]) => void
  onFilesUploaded?: (files: File[]) => void
  onDocumentIdsCreated?: (ids: string[]) => void
  onCompletenessChange?: (completeness: number) => void
  customerId?: string | null
  organizationId?: string | null
  currentExtractedData?: IssueRecorderOutput['extractedData']
  customer?: Customer | null
  entity?: CustomerEntity | null
  reportedBy?: CustomerUser | null
}

export function IssueRecordingChat({
  onDataExtracted, onComplete, onMessagesChange, onFilesUploaded, onDocumentIdsCreated, onCompletenessChange,
  customerId, organizationId, currentExtractedData = {},
  customer = null, entity = null, reportedBy = null,
}: IssueRecordingChatProps): JSX.Element {
  const [quickReplies, setQuickReplies] = useState<string[]>([])
  const [allowMultiSelect, setAllowMultiSelect] = useState(false)
  const [completeness, setCompleteness] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)
  const [piiItems, setPiiItems] = useState<PIIItem[] | null>(null)
  const [piiRedactedText, setPiiRedactedText] = useState('')
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null)
  const [pendingContent, setPendingContent] = useState('')

  const agentId = getAgentForFeature(
    'issue_recorder',
    import.meta.env.VITE_KAYCOO_ISSUE_RECORDER_AGENT_ID || 'd365a1b2-1111-5000-9000-15511e0ec07d',
  )

  // Build full context: customer profile + already-extracted conversation data
  const contextProvider = useCallback(() => {
    const parts: string[] = []

    // Customer profile context (version, products, deployment, config notes, agent instructions)
    const profileCtx = buildCustomerProfileContext(customer, entity, reportedBy)
    if (profileCtx) parts.push(profileCtx)

    // Additional extracted data from conversation (beyond what profile provides)
    const fields = currentExtractedData
    const extraParts: string[] = []
    if (fields.problemTitle) extraParts.push(`Issue Title: ${fields.problemTitle}`)
    if (fields.problemDescription) extraParts.push(`Issue Description: ${fields.problemDescription}`)
    if (fields.affectedModules) {
      const modules = Array.isArray(fields.affectedModules) ? fields.affectedModules.join(', ') : String(fields.affectedModules)
      if (modules) extraParts.push(`Affected Modules: ${modules}`)
    }

    if (extraParts.length > 0) {
      parts.push(`[EXTRACTED FROM CONVERSATION]\n${extraParts.join('\n')}\n[END EXTRACTED]`)
    }

    return parts.join('\n\n')
  }, [customer, entity, reportedBy, currentExtractedData])

  const buildGreeting = useCallback((c: Customer | null, e: CustomerEntity | null): string => {
    return c
      ? `Hello! I see we're recording an issue for **${c.name}**${e?.d365_version ? ` (D365 F&O ${e.d365_version})` : ''}. Please describe the problem you're experiencing, and attach any relevant documents or screenshots.`
      : "Hi! Please select a customer above, then describe the D365 F&O issue you're experiencing."
  }, [])

  const { messages, isTyping, error, sendMessage, resetMessages, cancelStreaming } = useChat({
    agentId,
    contextProvider,
    sessionKey: customer?.id ?? undefined,
    initialMessages: [{
      role: 'assistant',
      content: buildGreeting(customer, entity),
      timestamp: new Date().toISOString(),
    }],
  })

  // Reset chat when customer selection changes
  const prevCustomerIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const newId = customer?.id ?? null
    // Skip initial render (undefined → first value)
    if (prevCustomerIdRef.current === undefined) {
      prevCustomerIdRef.current = newId
      return
    }
    if (prevCustomerIdRef.current !== newId) {
      prevCustomerIdRef.current = newId
      resetMessages([{
        role: 'assistant',
        content: buildGreeting(customer, entity),
        timestamp: new Date().toISOString(),
      }])
      setQuickReplies([])
      setAllowMultiSelect(false)
      setCompleteness(0)
    }
  }, [customer?.id, customer, entity, resetMessages, buildGreeting])

  useEffect(() => { onMessagesChange?.(messages) }, [messages]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')
    if (lastAssistant) {
      const parsed = parseIssueRecorderResponse(lastAssistant.content)
      console.log('[IssueRecordingChat] Parsed response:', {
        allowMultiSelect: parsed.allowMultiSelect,
        quickReplies: parsed.suggestedQuickReplies,
        content: lastAssistant.content.substring(0, 200),
      })
      setQuickReplies(parsed.suggestedQuickReplies ?? [])
      setAllowMultiSelect(parsed.allowMultiSelect ?? false)
      const newCompleteness = parsed.completeness ?? 0
      setCompleteness(newCompleteness)
      onCompletenessChange?.(newCompleteness)
      if (parsed.extractedData && Object.keys(parsed.extractedData).length > 0) {
        onDataExtracted(parsed.extractedData)
      }
      if (parsed.nextAction === 'complete') {
        setShowSuccess(true)
        setTimeout(() => onComplete(), 2000)
      }
    }
  }, [messages]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendMessage = async (content: string, files?: File[]): Promise<void> => {
    let enrichedContent = content

    if (files && files.length > 0) {
      const fileContext = await buildFileContext(files)
      if (fileContext) {
        enrichedContent = content ? `${content}\n\n${fileContext}` : fileContext
      }
    }

    // Scan ALL content for PII when files attached (text messages + file content)
    if (files && files.length > 0 && enrichedContent.trim().length > 10) {
      try {
        const piiResult = await detectPII(enrichedContent)
        // Always show approval modal — even if no PII found, user must confirm
        setPiiItems(piiResult.pii_found)
        setPiiRedactedText(piiResult.redacted_text)
        setPendingFiles(files)
        setPendingContent(content)
        return
      } catch (err) {
        console.warn('PII detection failed, proceeding without redaction:', err)
      }
    }

    // No files or PII scan failed — proceed normally
    if (files && files.length > 0) {
      onFilesUploaded?.(files)
      const docIds = await uploadIssueFiles(files, customerId ?? null, organizationId ?? '')
      if (docIds.length > 0) onDocumentIdsCreated?.(docIds)
    }

    await sendMessage(enrichedContent)
  }

  const handlePIIApproval = async (decisions: PIIDecision[]) => {
    const files = pendingFiles
    const content = pendingContent
    setPiiItems(null)
    setPendingFiles(null)
    setPendingContent('')

    // Apply redactions to the content
    const redactedContent = applyRedactions(
      content,
      decisions.map((d) => ({ decision: d.decision, item: d.item })),
    )

    // Build enriched content with redacted file context
    let enrichedContent = redactedContent
    if (files && files.length > 0) {
      onFilesUploaded?.(files)
      const docIds = await uploadIssueFiles(files, customerId ?? null, organizationId ?? '')
      if (docIds.length > 0) onDocumentIdsCreated?.(docIds)
      const fileContext = await buildFileContext(files)
      if (fileContext) {
        const redactedFileContext = applyRedactions(fileContext, decisions.map((d) => ({ decision: d.decision, item: d.item })))
        enrichedContent = redactedContent ? `${redactedContent}\n\n${redactedFileContext}` : redactedFileContext
      }
    }

    await sendMessage(enrichedContent)
  }

  const handlePIICancel = () => {
    setPiiItems(null)
    setPendingFiles(null)
    setPendingContent('')
  }

  return (
    <Card className="flex h-full flex-col border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="border-b border-kaycoo-white-3 py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-kaycoo-teal-dark">Issue Recording</CardTitle>
          {isTyping && (
            <Button variant="ghost" size="sm" onClick={cancelStreaming} className="text-kaycoo-burgundy hover:bg-kaycoo-burgundy-pale">
              <X className="mr-1 h-4 w-4" /> Cancel
            </Button>
          )}
        </div>
        {completeness > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-kaycoo-charcoal-2">
              <span>Issue completeness</span>
              <span>{completeness}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-kaycoo-white-2">
              <div className="h-full bg-kaycoo-teal transition-all duration-300" style={{ width: `${completeness}%` }} />
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-hidden p-0">
        {showSuccess && (
          <Alert className="mx-4 mt-2 border-kaycoo-teal bg-kaycoo-teal-pale">
            <CheckCircle className="h-4 w-4 text-kaycoo-teal-dark" />
            <AlertDescription className="text-kaycoo-teal-dark">Issue recorded!</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive" className="mx-4 mt-2 border-kaycoo-burgundy-dark bg-kaycoo-burgundy-pale">
            <AlertTriangle className="h-4 w-4 text-kaycoo-burgundy-dark" />
            <AlertDescription className="text-kaycoo-burgundy-dark">{error.message || 'An error occurred'}</AlertDescription>
          </Alert>
        )}
        <ChatScrollHandler messages={messages} isTyping={isTyping} className="flex-1 space-y-4 overflow-y-auto px-4">
          {messages.map((msg, i) => <ChatMessage key={i} role={msg.role} content={msg.content} timestamp={msg.timestamp} />)}
          {isTyping && <TypingIndicator />}
        </ChatScrollHandler>
        {quickReplies.length > 0 && (
          <QuickReplyButtons
            options={quickReplies}
            onSelect={(o) => {
              const message = Array.isArray(o) ? o.join(', ') : o
              handleSendMessage(message)
            }}
            disabled={isTyping}
            multiSelect={allowMultiSelect}
            className="px-4"
          />
        )}
        <ChatInput onSendMessage={handleSendMessage} disabled={isTyping} placeholder="Describe the issue or attach documents..." allowFileUpload />
      </CardContent>

      {piiItems !== null && (
        <PIIApprovalModal
          items={piiItems}
          redactedText={piiRedactedText}
          onApprove={handlePIIApproval}
          onCancel={handlePIICancel}
        />
      )}
    </Card>
  )
}
